import { useMemo } from 'react'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { getBundleDiscountOnCurrentOffer } from '@/features/bundleDiscount/bundleDiscount.utils'
import { getPriceMatchYearlySavings } from '@/features/priceMatching/priceMatching.utils'
import {
  type ProductOfferFragment,
  RedeemedCampaignTypeV2,
  type RedeemedCampaignV2Fragment,
} from '@/services/graphql/generated'
import { YOUNG_PET_CAMPAIGN_CODE } from '@/utils/discounts.utils'
import { Formatter } from '@/utils/formatter'
import { useFormatter } from '@/utils/useFormatter'
import { type DiscountTooltipProps } from './DiscountTooltip'

/**
 * Returns tooltip props describing the active discount with the highest priority for a given offer.
 *
 * Prioritized order:
 * 1) Price matching succeeded (Hedvig price is lower)
 * 2) Young pet
 * 3) Campaign code
 * 4) Bundle discount
 * 5) Price match failed (Hedvig price is higher of incomparable offers)
 * 6) Forever code (referral)
 *
 * @param selectedOffer - The offer whose discounts are evaluated
 * @param campaign - The redeemed campaign (if any)
 * @returns Tooltip props for the highest-priority discount, or null if none applies
 */

export const useDiscountTooltipProps = (
  selectedOffer: ProductOfferFragment,
  hasCollectedInsurelyData: boolean,
  campaign?: RedeemedCampaignV2Fragment,
) => {
  const { t } = useTranslation(['purchase-form', 'cart'])
  const formatter = useFormatter()

  const tooltipProps = useMemo(() => {
    for (const getter of tooltipPropsGetters) {
      const props = getter({ selectedOffer, campaign, hasCollectedInsurelyData, formatter, t })
      if (props) {
        return props
      }
    }

    return null
  }, [selectedOffer, hasCollectedInsurelyData, formatter, t, campaign])

  return tooltipProps
}

type TooltipPropsGetter = (params: {
  selectedOffer: ProductOfferFragment
  campaign?: RedeemedCampaignV2Fragment
  hasCollectedInsurelyData: boolean
  formatter: Formatter
  t: TFunction<['purchase-form', 'cart']>
}) => Pick<DiscountTooltipProps, 'children' | 'color' | 'subtitle'> | null

const priceMatchSuccess: TooltipPropsGetter = ({ selectedOffer, formatter, t }) => {
  const yearlyReduction = getPriceMatchYearlySavings(selectedOffer, formatter)
  if (!yearlyReduction) return null

  return {
    children: t('PRICE_MATCH_BUBBLE_SUCCESS_TITLE', { amount: yearlyReduction }),
    color: 'green',
  }
}

const youngPetDiscount: TooltipPropsGetter = ({ selectedOffer }) => {
  const discount = selectedOffer.cost.discountsV2.find(
    ({ itemDiscount: discount }) => discount.campaignCode === YOUNG_PET_CAMPAIGN_CODE,
  )

  return discount
    ? {
        children: discount.itemDiscount.displayName,
        color: 'green',
      }
    : null
}

const campaignDiscount: TooltipPropsGetter = ({ selectedOffer, campaign }) => {
  if (!campaign) {
    return null
  }

  // Highlight active campaign discount if it applies to this offer
  const campaignDiscount = selectedOffer.cost.discountsV2.find(
    ({ itemDiscount: discount }) => discount.campaignCode === campaign.campaignCode,
  )

  return campaignDiscount
    ? {
        children: campaignDiscount.itemDiscount.displayName,
        color: 'green',
      }
    : null
}

const bundleDiscount: TooltipPropsGetter = ({ selectedOffer }) => {
  const discount = getBundleDiscountOnCurrentOffer(selectedOffer)

  return discount
    ? {
        children: discount.itemDiscount.displayName,
        color: 'green',
      }
    : null
}

const priceMatchFailed: TooltipPropsGetter = ({ selectedOffer, hasCollectedInsurelyData, formatter, t }) => {
  if (selectedOffer.priceMatch) {
    if (selectedOffer.priceMatch.priceReduction.amount < 1) {
      // We have the highest price so we don't price match
      const company = selectedOffer.priceMatch.externalInsurer.displayName
      const amount = formatter.monthlyPrice(selectedOffer.priceMatch.externalPrice)

      return {
        children: t('PRICE_MATCH_BUBBLE_INCOMPARABLE_TITLE', { amount, company }),
        color: 'gray',
      }
    } else {
      // Successful price match. Not the case we are interested in.
      return null
    }
  } else if (hasCollectedInsurelyData) {
    // You have opt into price matching but we can't price match you because the products are incomparable or
    // there's not enough data available to price match
    return {
      children: t('PRICE_MATCH_BUBBLE_NOT_POSSIBLE_TITLE'),
      color: 'gray',
    }
  }

  return null
}

const referralDiscount: TooltipPropsGetter = ({ campaign }) => {
  // This covers referral discounts that are applied to the cart
  // In case we have a 'campaign' but it's not a referral we don't show the tooltip
  // because that campaign doesn't affect the price of the selected offer. The case
  // where the campaign does affect the price of the selected offer is handled in the
  // `getHighlightedDiscount` function.
  if (campaign?.type === RedeemedCampaignTypeV2.Referral) {
    return {
      children: campaign.description,
      color: 'green',
    }
  }

  return null
}

// Order matters here. To change the priority of the highlightable things, you just need to change the order of the getters.
// If you need to handle a different case, just plug a new getter to this list in the order of your choice..
const tooltipPropsGetters = [
  priceMatchSuccess,
  youngPetDiscount,
  campaignDiscount,
  bundleDiscount,
  priceMatchFailed,
  referralDiscount,
] satisfies Array<TooltipPropsGetter>
